import React from "react";
import { Slider, TextField } from "@mui/material";
import styles from "./index.module.scss";
import rfStyles from "../RenderFields/index.module.scss";
import { ProgramQuestionMap } from "../../../pages/Registration";
import { ENT_TEXT_CONSTANTS } from "../../../constants/entTextConstants";

type ValueObj = Record<string, number | string | null>;

interface MultipleInputProps {
  pqm: ProgramQuestionMap;
  value?: ValueObj | string | null;
  onChange: (parentQuestionId: number, value: ValueObj | string | null) => void;
  inputClassName?: string;
  textAreaClassName?: string;
  error?: string;
  helperText?: string;
  onBlur?: () => void; // Add this prop
}

const toValueObj = (value?: ValueObj | string | null): ValueObj => {
  if (value == null) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return value;
};

const isEmptyVal = (v: unknown) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

const clamp = (n: number, min?: number, max?: number) => {
  if (min != null && n < min) return min;
  if (max != null && n > max) return max;
  return n;
};

const MultipleInput: React.FC<MultipleInputProps> = ({
  pqm,
  value,
  onChange,
  inputClassName,
  textAreaClassName,
  error,
  helperText,
  onBlur, // Add this prop
}) => {
  const { question } = pqm;
  const { config } = question || {};
  const { questions = [], isRequired, displayLabelType: parentDisplayType = "slider" } = config || {};

  const disabled = !!config?.isDisable;
  const current = React.useMemo(() => toValueObj(value), [value]);

  const [touched, setTouched] = React.useState(false);

  const handleItemChange = React.useCallback(
    (itemKey: string, next: string | number | null) => {
      const updated: ValueObj = { ...current, [itemKey]: next };
      setTouched(true);
      // Automatically stringify the multiple question answers
      onChange(question.id, JSON.stringify(updated));
    },
    [current, onChange, question.id]
  );

  if (!question || !config || !Array.isArray(questions) || questions.length === 0) {
    return null;
  }

  // Compute required completeness across all sub-questions
  const allRequiredSatisfied = React.useMemo(() => {
    if (!isRequired) return true;
    return questions.every((q: any) => {
      const mode = q.displayLabelType || parentDisplayType;
      const raw = current[q.key];
      if (mode === "slider") {
        return typeof raw === "number" && Number.isFinite(raw);
      }
      if (mode === "text" || mode === "textarea") {
        if (isEmptyVal(raw)) return false;
        const s = String(raw);
        const minChars = typeof q.minCharacter === "number" ? q.minCharacter : undefined;
        if (minChars != null && s.trim().length < minChars) return false;
        return true;
      }
      return !isEmptyVal(raw);
    });
  }, [current, isRequired, questions, parentDisplayType]);

  const internalRequiredError =
  !error && touched && isRequired && !allRequiredSatisfied ? ENT_TEXT_CONSTANTS.ERROR_ALL_FIELDS_REQUIRED : "";

  const renderField = (q: any, idx?: number) => {
    const min = typeof q.min === "number" ? q.min : undefined;
    const max = typeof q.max === "number" ? q.max : undefined;

    const maxChars =
      typeof q.maxCharacters === "number"
        ? q.maxCharacters
        : (Array.isArray(questions) &&
           typeof questions[idx || 0]?.maxCharacters === "number"
            ? questions[idx || 0].maxCharacters
            : undefined);

    const raw = current[q.key];
    const mode = q.displayLabelType || parentDisplayType;
    const numericValue =
      typeof raw === "number"
        ? raw
        : typeof raw === "string" && raw.trim() !== ""
        ? Number(raw)
        : undefined;

    const effectiveValue =
      typeof numericValue === "number"
        ? numericValue
        : typeof min === "number"
        ? min
        : 0;

    if (mode === "slider") {
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 80px",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Slider
            value={effectiveValue}
            min={min}
            max={max}
            step={1}
            onChange={(_, newVal) => {
              const v = Array.isArray(newVal) ? newVal[0] : newVal;
              const n = clamp(Number(v), min, max);
              handleItemChange(q.key, Number.isFinite(n) ? n : null);
            }}
            disabled={disabled}
            aria-required={!!isRequired}
          />
          <TextField
            type="number"
            size="small"
            value={
              typeof numericValue === "number"
                ? numericValue
                : typeof min === "number"
                ? min
                : ""
            }
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                handleItemChange(q.key, "");
                return;
              }
              const n = Number(val);
              if (!Number.isFinite(n)) return;
              handleItemChange(q.key, clamp(n, min, max));
            }}
            onBlur={(e) => {
              const val = e.target.value;
              if (val === "") return;
              const n = clamp(Number(val), min, max);
              setTouched(true);
              handleItemChange(q.key, n);
            }}
            inputProps={{
              min: min,
              max: max,
              step: 1,
              style: { textAlign: "right" },
              name: q.key,
            }}
            disabled={disabled}
            aria-required={!!isRequired}
          />
        </div>
      );
    }

    if (mode === "text") {
      return (
        <input
          type="text"
          className={inputClassName}
          name={q.key}
          value={
            raw == null
              ? ""
              : typeof raw === "number"
              ? String(raw)
              : String(raw)
          }
          placeholder={min != null && max != null ? `${min} - ${max}` : `Enter ${q.label}`}
          onChange={(e) => {
            let next = e.target.value;
            // Enforce numeric if min/max present
            if (min != null || max != null) {
              if (next.trim() === "") {
                handleItemChange(q.key, "");
                return;
              }
              if (!/^\d+$/.test(next)) return;
              const n = clamp(Number(next), min, max);
              handleItemChange(q.key, n);
            } else {
              // Enforce maxCharacters
              if (typeof maxChars === "number" && next.length > maxChars) {
                next = next.slice(0, maxChars);
              }
              handleItemChange(q.key, next);
            }
          }}
          onBlur={() => {
            setTouched(true);
          }}
          disabled={disabled}
          aria-required={!!isRequired}
        />
      );
    }

    if (mode === "textarea") {
      return (
        <>
          <textarea
            className={`${inputClassName || ""} ${textAreaClassName || ""}`}
            name={q.key}
            value={raw == null ? "" : String(raw)}
            placeholder={`Enter ${(q.label).toLowerCase()}`}
            onChange={(e) => {
              let next = e.target.value;
              if (typeof maxChars === "number" && next.length > maxChars) {
                next = next.slice(0, maxChars);
              }
              handleItemChange(q.key, next);
            }}
            onBlur={() => {
              setTouched(true);
            }}
            disabled={disabled}
            aria-required={!!isRequired}
          />
          {typeof maxChars === "number" && (
            <div className={rfStyles.helperTextContainer}>
              {typeof maxChars === "number" && (
                <p className={rfStyles.charCount}>
                  {Math.max(0, maxChars - String(raw ?? "").length)} characters left
                </p>
              )}
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div className={styles.multiInputLabel} onBlur={onBlur}>
          <div style={{ display: "grid", gap: 12 }}>
        {questions.map((q: any, idx: number) => {
          const mode = q.displayLabelType || parentDisplayType;
          return (
            <div
              key={q.key}
              className={styles.questionRow}
              style={{
                display: "grid",
                gridTemplateColumns: mode === "text" || mode === "textarea" ? "1fr" : "20% 50%",
                alignItems: "center",
                gap: 12,
              }}
            >
              {mode !== "text" && mode !== "textarea" && (
                <div className={styles.multiInputLabel}>{q.label}</div>
              )}
              {renderField(q, idx)}
            </div>
          );
        })}
      </div>

      {(helperText || error || internalRequiredError) && (
        <div className={rfStyles.helperTextContainer}>
          {helperText && <span className={rfStyles.roommatePreference}>{helperText}</span>}
          {(error || internalRequiredError) && (
            <p className={`${rfStyles.fieldError} ${rfStyles.helperError}`}>
              {error || internalRequiredError}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MultipleInput;